import { useCallback, useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { trackEvent } from '../lib/gamification';
import { apiErrorMessage, handlePlanLimitError } from '../lib/planLimit';
import { DOC_TYPES } from '../lib/docTypes';
import { 
  ArrowLeft, Upload, FileText, Search, FolderOpen, Plus, Trash2,
  Download, Eye, Loader2, File, Image, FileType, X, Check,
  Building2, Briefcase, Heart, Wallet, CreditCard, User, MoreHorizontal, ScanLine
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryConfig = {
  impots: { icon: CreditCard, color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'Impôts' },
  logement: { icon: Building2, color: 'text-purple-400', bg: 'bg-purple-500/20', label: 'Logement' },
  travail: { icon: Briefcase, color: 'text-green-400', bg: 'bg-green-500/20', label: 'Travail' },
  sante: { icon: Heart, color: 'text-red-400', bg: 'bg-red-500/20', label: 'Santé' },
  banque: { icon: Wallet, color: 'text-yellow-400', bg: 'bg-yellow-500/20', label: 'Banque' },
  identite: { icon: User, color: 'text-cyan-400', bg: 'bg-cyan-500/20', label: 'Identité' },
  autre: { icon: FileText, color: 'text-gray-400', bg: 'bg-gray-500/20', label: 'Autre' }
};

const getFileIcon = (mimeType) => {
  if (mimeType?.includes('image')) return Image;
  if (mimeType?.includes('pdf')) return FileType;
  return File;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const DocumentsPage = () => {
  const { isDark } = useTheme();
  const { token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_category: {}, total_size_mb: 0 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchParams] = useSearchParams();
  const requestedType = DOC_TYPES[searchParams.get('type')] ? searchParams.get('type') : '';
  const [uploadForm, setUploadForm] = useState({ name: '', category: 'autre', description: '', doc_type: requestedType });
  const [previewDoc, setPreviewDoc] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrDialogOpen, setOcrDialogOpen] = useState(false);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.documents || []);
      setStats(response.data.stats || { total: 0, by_category: {}, total_size_mb: 0 });
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) loadDocuments();
  }, [token, loadDocuments]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Fichier trop volumineux (max 10MB)');
        return;
      }
      setSelectedFile(file);
      setUploadForm({
        ...uploadForm,
        name: uploadForm.doc_type ? DOC_TYPES[uploadForm.doc_type].label : file.name.replace(/\.[^/.]+$/, ''),
      });
      setUploadDialogOpen(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', uploadForm.name || selectedFile.name);
      formData.append('category', uploadForm.category);
      formData.append('description', uploadForm.description);
      if (uploadForm.doc_type) formData.append('doc_type', uploadForm.doc_type);

      const response = await axios.post(`${API_URL}/api/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success('Document ajouté !');
        setUploadDialogOpen(false);
        setSelectedFile(null);
        setUploadForm({ name: '', category: 'autre', description: '', doc_type: '' });
        loadDocuments();
        trackEvent(token, 'first_document');
      }
    } catch (error) {
      if (!handlePlanLimitError(error, navigate)) {
        toast.error(apiErrorMessage(error, 'Erreur lors de l\'upload'));
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document supprimé');
      loadDocuments();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename || doc.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleOCR = async (doc) => {
    setOcrLoading(doc.id);
    try {
      const response = await axios.post(
        `${API_URL}/api/documents/${doc.id}/ocr`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setOcrResult({
          docName: doc.name,
          text: response.data.text,
          detected_info: response.data.detected_info,
          truncated: response.data.truncated
        });
        setOcrDialogOpen(true);
        toast.success('Texte extrait avec succès !');
        trackEvent(token, 'ocr_first');
      }
    } catch (error) {
      if (!handlePlanLimitError(error, navigate)) {
        toast.error(apiErrorMessage(error, 'Erreur lors de l\'extraction OCR'));
      }
    } finally {
      setOcrLoading(null);
    }
  };

  // Filter documents
  const filteredDocs = documents.filter(doc => {
    if (filterCategory !== 'all' && doc.category !== filterCategory) return false;
    if (search && !doc.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className={`min-h-screen px-4 py-6 transition-colors duration-300 ${isDark ? 'bg-[#0F0F1A]' : 'bg-[#FAFBFC]'}`} data-testid="documents-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <Link 
          to="/dashboard" 
          className={`inline-flex items-center gap-2 mb-6 transition-colors ${
            isDark ? 'text-[#94A3B8] hover:text-[#81d8d0]' : 'text-gray-500 hover:text-[#81d8d0]'
          }`}
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={18} />
          <span>Retour au dashboard</span>
        </Link>

        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-[#81d8d0]/20 flex items-center justify-center">
              <FolderOpen className="w-6 h-6 text-[#81d8d0]" />
            </div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Coffre-fort Documents
            </h1>
          </div>
          <p className={`mb-8 ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
            Stocke et organise tous tes documents importants en toute sécurité
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <Card className={`p-4 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Documents</p>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.total}</p>
          </Card>
          <Card className={`p-4 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Espace utilisé</p>
            <p className={`text-2xl font-bold text-[#81d8d0]`}>{stats.total_size_mb} MB</p>
          </Card>
          <Card className={`p-4 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Catégories</p>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {Object.keys(stats.by_category || {}).length}
            </p>
          </Card>
          <Card className={`p-4 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Limite</p>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>100 MB</p>
          </Card>
        </motion.div>

        {/* Search and Upload */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="flex flex-col sm:flex-row gap-4 mb-6"
        >
          <div className="relative flex-1">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-[#64748B]' : 'text-gray-400'}`} size={18} />
            <Input
              placeholder="Rechercher un document..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`pl-10 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200'}`}
              data-testid="search-documents"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className={`w-[160px] ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200'}`}>
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              {Object.entries(categoryConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
            className="hidden"
            onChange={handleFileSelect}
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="bg-[#81d8d0] hover:bg-[#6bc4bc] text-[#0F0F1A] font-semibold"
            data-testid="upload-document"
          >
            <Upload className="w-4 h-4 mr-2" />
            {requestedType ? `Ajouter : ${DOC_TYPES[requestedType].label}` : 'Ajouter'}
          </Button>
        </motion.div>

        {/* Categories quick filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex gap-2 flex-wrap mb-6"
        >
          {Object.entries(categoryConfig).map(([key, config]) => {
            const count = stats.by_category?.[key] || 0;
            const Icon = config.icon;
            return (
              <button
                key={key}
                onClick={() => setFilterCategory(filterCategory === key ? 'all' : key)}
                className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-2 transition-all ${
                  filterCategory === key
                    ? `${config.bg} ${config.color} ring-2 ring-offset-2 ${isDark ? 'ring-offset-[#0F0F1A]' : 'ring-offset-white'} ring-current`
                    : isDark ? 'bg-white/5 text-[#94A3B8] hover:bg-white/10' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Icon size={14} />
                {config.label}
                {count > 0 && <span className="opacity-60">({count})</span>}
              </button>
            );
          })}
        </motion.div>

        {/* Documents List */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#81d8d0]" />
            </div>
          ) : filteredDocs.length === 0 ? (
            <Card className={`p-12 text-center border border-dashed ${
              isDark ? 'bg-white/5 border-white/20' : 'bg-gray-50 border-gray-300'
            }`}>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                isDark ? 'bg-[#81d8d0]/10' : 'bg-[#81d8d0]/20'
              }`}>
                <FileText className="w-8 h-8 text-[#81d8d0]" />
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {search || filterCategory !== 'all' ? 'Aucun résultat' : 'Aucun document'}
              </h3>
              <p className={`mb-6 max-w-md mx-auto ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
                {search || filterCategory !== 'all' 
                  ? 'Essaie avec d\'autres critères de recherche'
                  : 'Commence par ajouter tes documents importants : avis d\'imposition, fiches de paie, contrats...'}
              </p>
              {!search && filterCategory === 'all' && (
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-[#FF6B6B] hover:bg-[#ff5252] text-white"
                  data-testid="add-first-document"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter mon premier document
                </Button>
              )}
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredDocs.map((doc) => {
                const catConfig = categoryConfig[doc.category] || categoryConfig.autre;
                const CatIcon = catConfig.icon;
                const FileIcon = getFileIcon(doc.mime_type);
                
                return (
                  <Card 
                    key={doc.id}
                    className={`p-4 border transition-all hover:scale-[1.02] ${
                      isDark ? 'bg-white/5 border-white/10 hover:border-[#81d8d0]/40' : 'bg-white border-gray-200 hover:border-[#81d8d0]/60'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${catConfig.bg}`}>
                        <FileIcon className={catConfig.color} size={24} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {doc.name}
                        </h4>
                        {DOC_TYPES[doc.doc_type] && (
                          <span className="inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#81d8d0]/20 text-[#0b6b63]">
                            {DOC_TYPES[doc.doc_type].label}
                          </span>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${catConfig.bg} ${catConfig.color}`}>
                            {catConfig.label}
                          </span>
                          <span className={`text-xs ${isDark ? 'text-[#64748B]' : 'text-gray-400'}`}>
                            {formatFileSize(doc.file_size)}
                          </span>
                        </div>
                        {doc.description && (
                          <p className={`text-xs mt-2 truncate ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>
                            {doc.description}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className={`flex items-center gap-2 mt-4 pt-3 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOCR(doc)}
                        disabled={ocrLoading === doc.id}
                        className={`flex-1 ${isDark ? 'text-[#94A3B8] hover:text-purple-400 hover:bg-purple-500/10' : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'}`}
                        title="Extraire le texte (OCR)"
                      >
                        {ocrLoading === doc.id ? (
                          <Loader2 size={16} className="mr-1 animate-spin" />
                        ) : (
                          <ScanLine size={16} className="mr-1" />
                        )}
                        OCR
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownload(doc)}
                        className={`flex-1 ${isDark ? 'text-[#94A3B8] hover:text-[#81d8d0] hover:bg-white/5' : 'text-gray-600 hover:text-[#81d8d0]'}`}
                      >
                        <Download size={16} className="mr-1" />
                        Télécharger
                      </Button>
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          isDark ? 'hover:bg-red-500/20 text-red-400' : 'hover:bg-red-50 text-red-500'
                        }`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8"
        >
          <Card className={`p-5 border ${isDark ? 'bg-[#81d8d0]/5 border-[#81d8d0]/20' : 'bg-[#81d8d0]/5 border-[#81d8d0]/20'}`}>
            <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              💡 Documents à conserver
            </h4>
            <ul className={`text-sm space-y-1 ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
              <li>• <strong>Impôts</strong> : Avis d'imposition (3 ans), déclarations</li>
              <li>• <strong>Travail</strong> : Contrats, fiches de paie (jusqu'à retraite), certificats</li>
              <li>• <strong>Logement</strong> : Bail, quittances (3 ans), état des lieux</li>
              <li>• <strong>Santé</strong> : Carte vitale, attestations, ordonnances importantes</li>
            </ul>
          </Card>
        </motion.div>

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent className={isDark ? 'bg-[#1A1A2E] border-white/10' : 'bg-white'}>
            <DialogHeader>
              <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
                Ajouter un document
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {selectedFile && (
                <div className={`p-3 rounded-lg flex items-center gap-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <File className="text-[#81d8d0]" size={24} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {selectedFile.name}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-[#64748B]' : 'text-gray-500'}`}>
                      {formatFileSize(selectedFile.size)}
                    </p>
                  </div>
                  <Check className="text-green-500" size={20} />
                </div>
              )}
              
              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Nom du document</Label>
                <Input
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                  className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                  placeholder="Ex: Avis d'imposition 2025"
                />
              </div>
              
              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Type de pièce</Label>
                <select
                  data-testid="upload-doc-type"
                  value={uploadForm.doc_type}
                  onChange={(e) => {
                    const t = e.target.value;
                    setUploadForm({ ...uploadForm, doc_type: t, category: t ? DOC_TYPES[t].category : uploadForm.category });
                  }}
                  className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                >
                  <option value="">Autre document</option>
                  {Object.entries(DOC_TYPES).map(([key, t]) => <option key={key} value={key}>{t.label}</option>)}
                </select>
                <p className={`text-xs mt-1 ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Typée, la pièce est reprise dans tes démarches (APL, prime d'activité…).</p>
              </div>

              {!uploadForm.doc_type && (
              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Catégorie</Label>
                <Select value={uploadForm.category} onValueChange={(v) => setUploadForm({ ...uploadForm, category: v })}>
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}
              
              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Description (optionnel)</Label>
                <Input
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                  placeholder="Ex: Déclaration revenus 2024"
                />
              </div>
              
              <Button
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
                className="w-full bg-[#81d8d0] hover:bg-[#6bc4bc] text-[#0F0F1A]"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Upload en cours...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Ajouter le document
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* OCR Result Dialog */}
        <Dialog open={ocrDialogOpen} onOpenChange={setOcrDialogOpen}>
          <DialogContent className={`max-w-2xl ${isDark ? 'bg-[#1A1A2E] border-white/10' : 'bg-white'}`}>
            <DialogHeader>
              <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
                Texte extrait — {ocrResult?.docName}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {/* Detected Info */}
              {ocrResult?.detected_info?.fields?.length > 0 && (
                <div className={`p-4 rounded-lg ${isDark ? 'bg-[#81d8d0]/10 border border-[#81d8d0]/20' : 'bg-[#81d8d0]/10 border border-[#81d8d0]/20'}`}>
                  <h4 className={`text-sm font-medium mb-2 ${isDark ? 'text-[#81d8d0]' : 'text-[#0d9488]'}`}>
                    ✨ Informations détectées automatiquement
                  </h4>
                  {ocrResult.detected_info.document_type && (
                    <p className={`text-sm mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <strong>Type :</strong> {ocrResult.detected_info.document_type}
                    </p>
                  )}
                  <div className="space-y-1">
                    {ocrResult.detected_info.fields.map((field, idx) => (
                      <p key={idx} className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
                        <strong>{field.label} :</strong> {field.value || field.values?.join(', ')}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Raw Text */}
              <div>
                <h4 className={`text-sm font-medium mb-2 ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
                  Texte brut extrait {ocrResult?.truncated && '(tronqué)'}
                </h4>
                <div className={`p-4 rounded-lg font-mono text-xs whitespace-pre-wrap max-h-[300px] overflow-y-auto ${
                  isDark ? 'bg-black/30 text-[#e2e8f0]' : 'bg-gray-100 text-gray-800'
                }`}>
                  {ocrResult?.text || 'Aucun texte extrait'}
                </div>
              </div>
              
              {/* Copy button */}
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(ocrResult?.text || '');
                  toast.success('Texte copié');
                }}
                variant="outline"
                className={`w-full ${isDark ? 'border-white/20' : ''}`}
              >
                Copier le texte
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DocumentsPage;
